<div class="email-outreach-container surface-section p-3">
  <p-toast [life]="5000" />
  <p-confirmdialog />
  <div *ngIf="isSaving || sequencePreparationLoading" class="loading-overlay">
    <p-progress-spinner aria-label="Loading"></p-progress-spinner>
    <p class="loading-text">{{ loadingMessage }}</p>
  </div>
  
  <!-- Company Info Header -->
  <div class="company-info-header">
    <app-company-header 
      [isLoading]="isLoading" 
      (refreshClicked)="refreshData()">
    </app-company-header>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading && !dataLoaded" class="loading-container">
    <div class="grid">
      <!-- Loading Skeleton for Header -->
      <div class="col-12">
        <div class="page-header-skeleton">
          <div class="flex justify-content-between align-items-center mb-4">
            <div>
              <p-skeleton height="2rem" width="200px" class="mb-2"></p-skeleton>
              <p-skeleton height="1rem" width="300px"></p-skeleton>
            </div>
            <div class="flex gap-2">
              <p-skeleton height="2.5rem" width="120px"></p-skeleton>
              <p-skeleton height="2.5rem" width="120px"></p-skeleton>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading Skeleton for Tab Menu -->
      <div class="col-12">
        <p-skeleton height="3rem" class="mb-4"></p-skeleton>
      </div>

      <!-- Loading Skeleton for Table -->
      <div class="col-12">
        <div class="p-card">
          <div class="p-card-body">
            <p-skeleton height="2rem" class="mb-3"></p-skeleton>
            <p-skeleton height="1.5rem" class="mb-2" *ngFor="let item of [1,2,3,4,5]"></p-skeleton>
          </div>
        </div>
      </div>
    </div>

    <div class="loading-message">
      <i class="pi pi-spin pi-spinner text-4xl text-primary mb-3"></i>
      <p class="text-lg">{{ getLoadingMessage() }}</p>
    </div>
  </div>

  <!-- No Company Selected State -->
  <div *ngIf="!currentCompany && !isLoading" class="no-company-container">
    <i class="pi pi-spinner pi-spin text-6xl text-color-secondary mb-4"></i>
    <p class="text-color-secondary">Loading company data...</p>
  </div>

  <!-- Main Email Outreach Content (when company is selected and data is loaded) -->
  <div *ngIf="currentCompany && (dataLoaded || !isLoading)" class="email-content">

    <div class="page-header flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-4">
      <div class="header-left flex flex-column">
        <h1>Email Outreach</h1>
        <p>Create, manage, and track email sequences</p>
      </div>
      <div class="header-actions">
        <button *ngIf="canImportProspects" pButton pRipple label="Import Prospects" icon="pi pi-download" class="p-button-secondary mr-2"
          (click)="showImportDialogFunc()"></button>
        <button *ngIf="activeView === 'sequences' && !editingSequence && !creatingNewSequence && !editingTemplate && !creatingNewTemplate && canCreateSequences" pButton pRipple label="New Sequence" icon="pi pi-plus"
          class="p-button-primary" (click)="showNewSequenceForm($event)"></button>
        <button *ngIf="activeView === 'templates' && !editingSequence && !creatingNewSequence && !editingTemplate && !creatingNewTemplate && canCreateTemplates" pButton pRipple label="New Template" icon="pi pi-plus"
          class="p-button-primary" (click)="showNewTemplateForm()"></button>
        <span *ngIf="!canCreateSequences && !canImportProspects && !canCreateTemplates" class="text-600 text-sm p-3">
          Read-only access - contact your administrator to create content
        </span>
      </div>
    </div>

    <!-- Main Navigation Tabs -->
    <p-tabMenu [model]="tabs" [activeItem]="activeTab" (activeItemChange)="onTabChange($event)"></p-tabMenu>

    <!--  SEQUENCES VIEW -->
    <div
      *ngIf="activeView === 'sequences' && !editingSequence && !creatingNewSequence && !editingTemplate && !creatingNewTemplate"
      class="main-content">
      <app-email-sequence-table
        [sequences]="sequences"
        [loading]="loading"
        [currentCompany]="currentCompany"
        [selectedIntegration]="selectedIntegration"
        [canEditSequences]="canEditSequences"
        [canDeleteSequences]="canDeleteSequences"
        [canManageSequences]="canManageSequences"
        (sequenceStarted)="startSequence($event)"
        (sequencePaused)="pauseSequence($event)"
        (sequenceEdited)="editSequence($event)"
        (sequenceDeleted)="deleteSequence($event.event, $event.sequence)"
        (sequenceMetricsViewed)="openSequenceModal($event)"
        (sequenceDetailsViewed)="navigateToSequenceDetails($event)"
        (prospectModalShown)="showProspectSelectModal($event)"
        (newSequenceClicked)="showNewSequenceForm($event)">
      </app-email-sequence-table>
    </div>

    <!-- TEMPLATES VIEW -->
    <div
      *ngIf="activeView === 'templates' && !editingSequence && !creatingNewSequence && !editingTemplate && !creatingNewTemplate"
      class="main-content">
      <p-table [value]="templates" [loading]="templatesLoading" styleClass="p-datatable-gridlines p-datatable-sm"
        [paginator]="true" [rows]="10">
        <ng-template pTemplate="header">
          <tr>
            <th>Template Name</th>
            <th>Subject Line</th>
            <th>Created Date</th>
            <th>Last Modified</th>
            <th>Actions</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-template>
          <tr>
            <td>{{template.name}}</td>
            <td>{{template.subject}}</td>
            <td>{{template.createdAt | date:'MM-dd-yyyy'}}</td>
            <td>{{template.updatedAt | date:'MM-dd-yyyy'}}</td>
            <td>
              <div class="action-buttons">
                <button *ngIf="canEditTemplates" pButton pRipple icon="pi pi-pencil" class="p-button-outlined p-button-secondary p-button-rounded p-button-sm"
                  (click)="editTemplate(template)" pTooltip="Edit Template" tooltipPosition="top"></button>
                <button *ngIf="canCreateTemplates" pButton pRipple icon="pi pi-copy" class="p-button-outlined p-button-secondary p-button-rounded p-button-sm"
                  pTooltip="Duplicate Template" tooltipPosition="top"></button>
                <button *ngIf="canDeleteTemplates" pButton pRipple icon="pi pi-trash" class="p-button-outlined p-button-secondary p-button-rounded p-button-sm"
                  (click)="deleteTemplate(template)" pTooltip="Delete Template" tooltipPosition="top"></button>
                <span *ngIf="!canEditTemplates && !canDeleteTemplates" class="text-600 text-sm">
                  View only
                </span>
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="5" class="text-center">
              <div class="py-4">
                <i class="pi pi-file text-4xl text-color-secondary mb-3"></i>
                <p class="text-color-secondary">No email templates found for this company.</p>
                <button pButton pRipple label="Create First Template" icon="pi pi-plus" class="p-button-primary mt-3"
                  (click)="showNewTemplateForm()"></button>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <!-- TEMPLATE EDITING / CREATION FORM -->
    <app-new-email-template
      [visible]="editingTemplate || creatingNewTemplate"
      [editingTemplate]="editingTemplate"
      [currentCompany]="currentCompany"
      [template]="currentTemplate"
      (templateSaved)="onTemplateSaved()"
      (cancelled)="onTemplateCancelled()">
    </app-new-email-template>

    <!-- SEQUENCE CREATION FORM -->
    <app-new-email-sequence
      [visible]="editingSequence || creatingNewSequence"
      [editingSequence]="editingSequence"
      [currentCompany]="currentCompany"
      [integrations]="integrations"
      [templates]="templates"
      [prospects]="prospects"
      [emailTemplates]="emailTemplates"
      [selectedIntegration]="selectedIntegration"
      [platformEmailAccounts]="platformEmailAccounts"
      [sequence]="editingSequence ? getSequenceForEditing() : null"
      (sequenceSaved)="onSequenceSaved()"
      (cancelled)="onSequenceCancelled()"
      (integrationChanged)="onIntegrationChanged($event)"
      (prospectsLoaded)="onProspectsLoadedFromChild($event)">
    </app-new-email-sequence>

    <!-- PROSPECTS VIEW -->
    <div
      *ngIf="activeView === 'prospects' && !editingSequence && !creatingNewSequence && !editingTemplate && !creatingNewTemplate"
      class="main-content">
      <p-confirmDialog [style]="{width: '450px'}"></p-confirmDialog>

      <!-- Enhanced Empty State for Prospects -->
      <div *ngIf="!prospects || prospects.length === 0" class="text-center py-5">
        <i class="pi pi-users text-6xl text-color-secondary mb-4"></i>
        <h3 class="text-color">No Prospects Available</h3>
        <p class="text-color-secondary mb-4">Import prospects to get started with email outreach campaigns.</p>
        <button *ngIf="canImportProspects" pButton pRipple label="Import Prospects" icon="pi pi-download" class="p-button-primary"
          (click)="showImportDialogFunc()"></button>
        <p *ngIf="!canImportProspects" class="text-color-secondary">Contact your administrator to import prospects.</p>
      </div>

      <!-- Prospects Data View -->
      <p-dataView #dv [value]="prospects" [layout]="prospectLayout" *ngIf="prospects && prospects.length > 0">
        <ng-template pTemplate="grid" #grid let-prospects>
          <div class="grid">
            <div *ngFor="let prospect of prospects" class="col-12 md:col-6 lg:col-4 xl:col-3">
              <div class="prospect-list-card">
                <div class="card-content">
                  <div class="card-title-section">
                    <h3 class="list-title">{{prospect.name}}</h3>
                    <button pButton icon="pi pi-users custom-icon-size text-2xl"
                      class="p-button-rounded pi-lg p-button-text p-button-plain"></button>
                  </div>
                  <p class="prospect-count">{{prospect.count}} prospects</p>
                  <p class="creation-date">Created on {{prospect.updatedAt | date:'M/d/yyyy'}}</p>
                </div>
              </div>
            </div>
          </div>
        </ng-template>
      </p-dataView>
    </div>

  </div>
</div>

<!-- Import Dialog -->
<app-prospect-import-dialog [(visible)]="showImportDialog" (fileUploaded)="onProspectsImported($event)"
  (cancel)="onImportCancelled()">
</app-prospect-import-dialog>

<!-- Metrics Modal -->
<app-sequence-metrics-details-modal [(visible)]="showMetricsModal" [sequence]="selectedMetricsSequence"
  (visibleChange)="onModalClose($event)"></app-sequence-metrics-details-modal>

<app-prospect-select-modal [(visible)]="isProspectSelectModalVisible" [prospectLists]="availableProspects"
  [sequence]="selectedSequence" (listSelected)="onProspectListSelected($event)"
  (cancelled)="onProspectSelectModalCancelled()">
</app-prospect-select-modal>